import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  bankAccountSchema,
  BankAccountFormValues
} from '../../validation/paymentValidation'
import NxTextInput from 'components/inputs/NxTextInput'
import NxSelectInput from 'components/inputs/NxSelectInput'
import { usePaymentStore } from 'stores/paymentStore'
import SfCheckbox from 'components/overrides/SfCheckbox'
import Formatters from 'utils/formatters'

const AddBankAccount: React.FC = () => {

  const [ showErrors, setShowErrors ] = useState<boolean>( false )


  const {
    register,
    setError,
    formState
  } = useForm<BankAccountFormValues>({
    resolver: zodResolver( bankAccountSchema ),
    shouldFocusError: true,
    mode: `onChange`
  })

  const {
    setNewBankDetails,
    setIsNewBankValid,
    newBankDetails,
    savePaymentProfile,
    setSavePaymentProfile,
    splitPayment
  } = usePaymentStore()

  useEffect( () => {
    setNewBankDetails({
      routingNumber: ``,
      accountNumber: ``,
      accountType: ``,
      nameOnAccount: ``,
      confirmAccountNumber: ``
    })
    setIsNewBankValid( false )
  }, [] )

  const { errors } = formState

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    numberInput = false
  ) => {
    setShowErrors( false )

    const updatedValue = numberInput
      ? Formatters.numericCharacters( e.target.value )
      : e.target.value

    const updatedBankDetails = {
      ...newBankDetails,
      [e.target.name]: updatedValue
    }


    setNewBankDetails( updatedBankDetails )

    const valid = bankAccountSchema.safeParse( updatedBankDetails ).success && updatedBankDetails.confirmAccountNumber === updatedBankDetails.accountNumber

    setIsNewBankValid( valid )
  }

  const onBlur = async ( e: any ) => {
    if ( e.target.name === `confirmAccountNumber` && newBankDetails.confirmAccountNumber !== newBankDetails.accountNumber ) {
      setError( `confirmAccountNumber`, {
        message: `Confirm account number does not match account number`
      })
    }
    setShowErrors( true )
  }

  return (
    <div className="flex flex-col gap-4 pl-10 pr-4 mt-3 pb-8">
      <NxTextInput
        label={`Routing Number`}
        required
        data-testid="routing_number"
        type="text"
        maxLength={9}
        pattern="[0-9]*"
        id="routingNumber"
        name="routingNumber"
        onChange={( e ) => onChange( e, true )}
        value={newBankDetails.routingNumber}
        onBlur={onBlur}
        register={register}
        invalid={showErrors && Boolean( errors.routingNumber?.message )}
        error={showErrors ? errors.routingNumber?.message : ``}
      />
      <NxTextInput
        label={`Account Number`}
        required
        data-testid="account_number"
        type="text"
        pattern="[0-9]*"
        inputMode="numeric"
        maxLength={17}
        id="accountNumber"
        name="accountNumber"
        value={newBankDetails.accountNumber}
        onBlur={onBlur}
        register={register}
        onChange={( e ) => onChange( e, true )}
        invalid={showErrors && Boolean( errors.accountNumber?.message )}
        error={showErrors ? errors.accountNumber?.message : ``}
      />
      <NxTextInput
        label={`Confirm Account Number`}
        required
        data-testid="confirm_account_number"
        type="text"
        pattern="[0-9]*"
        maxLength={17}
        placeholder={( newBankDetails.accountNumber === undefined || Boolean( errors?.confirmAccountNumber?.message ) || newBankDetails.accountNumber?.length < 9 ) ? `Please Enter a Valid Account Number` : ``}
        id="confirmAccountNumber"
        name="confirmAccountNumber"
        value={newBankDetails.confirmAccountNumber}
        disabled={newBankDetails.accountNumber === undefined || Boolean( errors?.accountNumber?.message ) || newBankDetails.accountNumber?.length < 9}
        onBlur={onBlur}
        register={register}
        onChange={( e ) => onChange( e, true )}
        invalid={showErrors && Boolean( errors.confirmAccountNumber?.message )}
        error={showErrors ? errors.confirmAccountNumber?.message : ``}
      />
      <NxSelectInput
        label={`Account Type`}
        required
        data-testid="account_type"
        id="accountType"
        name="accountType"
        onBlur={onBlur}
        register={register}
        onChange={onChange}
        defaultValue=""
      >
        <option value="" disabled>{`Select Account Type`}</option>
        <option value="Checking">{`Checking`}</option>
        <option value="Savings">{`Savings`}</option>
      </NxSelectInput>
      <NxTextInput
        label={`Name on Account`}
        required
        data-testid="name_on_account"
        type="text"
        id="nameOnAccount"
        name="nameOnAccount"
        value={newBankDetails.nameOnAccount}
        onBlur={onBlur}
        register={register}
        onChange={onChange}
        invalid={showErrors && Boolean( errors.nameOnAccount?.message )}
        error={showErrors ? errors.nameOnAccount?.message : ``}
      />
      {!splitPayment && (
        <div
          className="flex items-center gap-3 cursor-pointer justify-start"
          onClick={() => {
            setSavePaymentProfile( !savePaymentProfile )
          }}
          data-testid="snl-bank-save-payment"
        >
          <SfCheckbox
            id="save-bank-profile"
            checked={savePaymentProfile}
            className="mt-1 min-w-[25px] h-[25px]"
          />
          <p className="font-light">{`Save Payment Details`}</p>
        </div>
      )}
    </div>
  )
}

export default AddBankAccount
